// @ts-nocheck
import { useState, useEffect, useRef } from 'react'
import { Send, Users, MessageCircle, X, Wifi, WifiOff } from 'lucide-react'
import { useChat } from '../hooks/useChat'
import { useAuthStore } from '../stores/authStore'

export default function ChatPanel({ isOpen, onClose }) {
  const [inputMessage, setInputMessage] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const messagesEndRef = useRef(null)
  const typingTimeoutRef = useRef(null)
  
  const { messages, onlineMembers, typingUsers, sendMessage, setTyping, connected, isLoadingHistory } = useChat()
  const user = useAuthStore((state) => state.user)

  // メッセージ送信
  const handleSend = async (e) => {
    e.preventDefault()
    if (!inputMessage.trim() || isSending) return

    setIsSending(true)
    try {
      await sendMessage(inputMessage)
      setInputMessage('')
      setTyping(false)
      setIsTyping(false)
    } catch (error) {
      console.error('[ChatPanel] メッセージ送信失敗:', error)
      alert('メッセージの送信に失敗しました')
    } finally {
      setIsSending(false)
    }
  }

  // タイピングインジケーター
  const handleInputChange = (e) => {
    setInputMessage(e.target.value)
    
    // 入力がある場合は常にタイピング状態を送信
    if (e.target.value.trim()) {
      if (!isTyping) {
        setIsTyping(true)
        setTyping(true)
      }

      // タイピング停止のタイムアウト
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false)
        setTyping(false)
      }, 3000) // 3秒に延長
    } else {
      // 入力が空の場合は即座にタイピング状態を解除
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
      setIsTyping(false)
      setTyping(false)
    }
  }

  // 自動スクロール（メッセージ更新時）
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, typingUsers])

  // チャットパネルが開かれたときに最新メッセージまでスクロール
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      // 少し遅延させてDOMの描画を待つ
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
      }, 100)
    }
  }, [isOpen, messages.length])

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
      if (isTyping) {
        setTyping(false)
      }
    }
  }, [isTyping, setTyping])

  // タイピング中のユーザー名を取得
  const getTypingUserNames = () => {
    const names = []
    typingUsers.forEach(userStr => {
      try {
        const userData = JSON.parse(userStr)
        if (userData.userId !== user?.sub) {
          names.push(userData.userName)
        }
      } catch (e) {
        console.error('[ChatPanel] タイピングユーザー解析エラー:', e)
      }
    })
    return names
  }

  if (!isOpen) return null

  const typingUserNames = getTypingUserNames()

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[600px] bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col z-50">
      {/* ヘッダー */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-t-lg">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h3 className="font-semibold text-gray-900 dark:text-white">チャット</h3>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            ({onlineMembers.length}人)
          </span>
        </div>
        <div className="flex items-center gap-2">
          {connected ? (
            <Wifi className="w-4 h-4 text-green-600 dark:text-green-400" title="接続中" />
          ) : (
            <WifiOff className="w-4 h-4 text-red-600 dark:text-red-400" title="切断" />
          )}
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="閉じる"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      </div>

      {/* オンラインメンバー */}
      <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-start gap-2 text-sm">
          <Users className="w-4 h-4 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-gray-600 dark:text-gray-400">オンライン: </span>
            <div className="flex gap-1 flex-wrap mt-1">
              {onlineMembers.length > 0 ? (
                onlineMembers.map(member => (
                  <span
                    key={member.userId}
                    className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs inline-flex items-center gap-1"
                    title={member.displayName}
                  >
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                    {member.displayName}
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  オンラインユーザーなし
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* メッセージリスト */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900/50">
        {isLoadingHistory ? (
          <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500 text-sm">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-gray-300 dark:border-gray-600 border-t-blue-600 rounded-full animate-spin mx-auto mb-2"></div>
              <p>メッセージを読み込み中...</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500 text-sm">
            <div className="text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>メッセージはまだありません</p>
              <p className="text-xs mt-1">最初のメッセージを送信してみましょう</p>
            </div>
          </div>
        ) : (
          messages.map((msg, index) => {
            const isOwnMessage = msg.userId === user?.sub
            return (
              <div
                key={index}
                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-lg p-3 shadow-sm ${
                    isOwnMessage
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600'
                  }`}
                >
                  {!isOwnMessage && (
                    <div className="text-xs font-semibold mb-1 opacity-70">
                      {msg.userName || '不明なユーザー'}
                    </div>
                  )}
                  <div className="text-sm whitespace-pre-wrap break-words">
                    {msg.message}
                  </div>
                  <div className={`text-xs mt-1 ${isOwnMessage ? 'opacity-70' : 'opacity-50'}`}>
                    {new Date(msg.timestamp).toLocaleTimeString('ja-JP', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              </div>
            )
          })
        )}
        
        {/* タイピングインジケーター */}
        {typingUserNames.length > 0 && (
          <div className="flex justify-start">
            <div className="bg-gray-200 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm text-gray-600 dark:text-gray-400 italic">
              {typingUserNames.join(', ')}が入力中
              <span className="inline-flex ml-1">
                <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
              </span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* 入力フォーム */}
      <form onSubmit={handleSend} className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputMessage}
            onChange={handleInputChange}
            placeholder={connected ? "メッセージを入力..." : "接続を待っています..."}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!connected || isSending}
            maxLength={1000}
          />
          <button
            type="submit"
            disabled={!inputMessage.trim() || !connected || isSending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center min-w-[44px]"
            title="送信"
          >
            {isSending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        {!connected && (
          <div className="text-xs text-red-500 dark:text-red-400 mt-2 flex items-center gap-1">
            <WifiOff className="w-3 h-3" />
            接続が切断されています
          </div>
        )}
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {inputMessage.length}/1000
        </div>
      </form>
    </div>
  )
}
